import {
  h,
  ref,
  provide,
  computed,
  defineComponent,
  getCurrentInstance,
  watch,
} from 'vue'
import { useLocale } from '@sgui-plus/hooks'
import { debugWarn } from '@sgui-plus/utils/error'
import { buildProps, definePropType } from '@sgui-plus/utils/props'
import { mutable } from '@sgui-plus/utils/types'
import { PageContextKey } from './constants'

import Prev from './components/prev.vue'
import Next from './components/next.vue'
import Sizes from './components/sizes.vue'
import Jumper from './components/jumper.vue'
import Total from './components/total.vue'
import Pager from './components/pager.vue'
import Pages from './components/pages.vue'
import Holder from './components/holder.vue'

import type { VNode, ExtractPropTypes } from 'vue'

/**
 * It it user's responsibility to guarantee that the value of props.total... is number
 * (same as pageSize, currentPage, pageCount)
 * Otherwise we can reasonable infer that the corresponding field is absent
 */
const isAbsent = (v: unknown): v is undefined => typeof v !== 'number'

type LayoutKey =
  | 'prev'
  | 'pager'
  | 'next'
  | 'jumper'
  | 'total'
  | 'sizes'
  | 'slot'
  | 'pages'
  | 'holder'

export const pageProps = buildProps({
  total: Number,
  pageSize: Number,
  defaultPageSize: Number,
  currentPage: Number,
  defaultCurrentPage: Number,
  pageCount: Number,
  pagerCount: {
    type: Number,
    validator: (value: unknown) => {
      return (
        typeof value === 'number' &&
        (value | 0) === value &&
        value > 4 &&
        value < 22 &&
        value % 2 === 1
      )
    },
    default: 7,
  },
  layout: {
    type: String,
    default: (
      ['total', 'holder', 'prev', 'pager', 'next', 'sizes'] as LayoutKey[]
    ).join(', '),
  },
  pageSizes: {
    type: definePropType<number[]>(Array),
    default: () => mutable([10, 20, 30, 40, 50, 100] as const),
  },
  popperClass: {
    type: String,
    default: '',
  },
  prevText: {
    type: String,
    default: '',
  },
  nextText: {
    type: String,
    default: '',
  },
  type: String,
  size: {
    type: String,
    validator: (value: unknown) => {
      return (
        typeof value === 'string' &&
        ['default', 'small'].includes(value)
      )
    },
    default: 'default'
  },
  disabled: Boolean,
  hideOnSinglePage: Boolean,
  placement: {
    type: String,
    default: 'bottom',
  },
  transfer: {
    type: Boolean,
    default: true,
  },
  activeColor: String,
  activeBackground : String,
  isLessPages: {
    type: Boolean,
    default: false,
  },
  showTotal: {
    type: Boolean,
    default: true,
  },
  showPages: {
    type: Boolean,
    default: false,
  },
  showJumper: {
    type: Boolean,
    default: false,
  },
  showSizes: {
    type: Boolean,
    default: false,
  },
  showFirstAndLast: {
    type: Boolean,
    default: false,
  },
  justify: {
    type: String,
    default: 'left'
  }
} as const)
export type PageProps = ExtractPropTypes<typeof pageProps>

export const pageEmits = {
  'update:current-page': (val: number) => typeof val === 'number',
  'update:page-size': (val: number) => typeof val === 'number',
  'on-change': (currentPage: number, pageSize: number) => typeof currentPage === 'number',
}
export type PageEmits = typeof pageEmits

const componentName = 'SgPage'
export default defineComponent({
  name: componentName,

  props: pageProps,
  emits: pageEmits,

  setup(props, { emit, slots }) {
    const { t } = useLocale()
    const vnodeProps = getCurrentInstance()!.vnode.props || {}
    // we can find @xxx="xxx" props on `vnodeProps` to check if user bind corresponding events
    const hasCurrentPageListener =
      'onUpdate:currentPage' in vnodeProps ||
      'onUpdate:current-page' in vnodeProps ||
      'onOnChange' in vnodeProps
    const hasPageSizeListener =
      'onUpdate:pageSize' in vnodeProps ||
      'onUpdate:page-size' in vnodeProps ||
      'onOnChange' in vnodeProps
    const assertValidUsage = computed(() => {
      // Users have to set either one, otherwise count of pages cannot be determined
      if (isAbsent(props.total) && isAbsent(props.pageCount)) return false
      // <sg-page ...otherProps :current-page="xxx" /> without corresponding listener is forbidden now
      // Users have to use two way binding of `currentPage`
      // If users just want to provide a default value, `defaultCurrentPage` is here for you
      if (!isAbsent(props.currentPage) && !hasCurrentPageListener) return false
      // When you want to change sizes, things get more complex, detailed below
      // Basically the most important value we need is page count
      // either directly from props.pageCount
      // or calculated from props.total
      // we will take props.pageCount precedence over props.total
      if (props.layout.includes('sizes')) {
        if (!isAbsent(props.pageCount)) {
          // if props.pageCount is assign by user, then user have to watch pageSize change
          // and recalculate pageCount
          if (!hasPageSizeListener) return false
        } else if (!isAbsent(props.total)) {
          // Otherwise, we will see if user have props.pageSize defined
          // If so, meaning user want to have pageSize controlled himself/herself from component
          // Thus page size listener is required
          // users are account for page size change
          if (!isAbsent(props.pageSize)) {
            if (!hasPageSizeListener) {
              return false
            }
          } else {
            // (else block just for explaination)
            // else page size is controlled by sg-page internally
          }
        }
      }
      return true
    })

    const innerPageSize = ref(
      isAbsent(props.defaultPageSize) ? 10 : props.defaultPageSize
    )
    const innerCurrentPage = ref(
      isAbsent(props.defaultCurrentPage) ? 1 : props.defaultCurrentPage
    )
    const pageSizeBridge = computed({
      get() {
        return isAbsent(props.pageSize) ? innerPageSize.value : props.pageSize
      },
      set(v: number) {
        if (isAbsent(props.pageSize)) {
          innerPageSize.value = v
        }
        if (hasPageSizeListener) {
          emit('update:page-size', v)
          emit('on-change', currentPageBridge.value, v)
        }
      },
    })

    const pageCountBridge = computed<number>(() => {
      let pageCount = 0
      if (!isAbsent(props.pageCount)) {
        pageCount = props.pageCount
      } else if (!isAbsent(props.total)) {
        pageCount = Math.max(1, Math.ceil(props.total / pageSizeBridge.value))
      }
      return pageCount
    })

    const currentPageBridge = computed<number>({
      get() {
        return isAbsent(props.currentPage)
          ? innerCurrentPage.value
          : props.currentPage
      },
      set(v) {
        let newCurrentPage = v
        if (v < 1) {
          newCurrentPage = 1
        } else if (v > pageCountBridge.value) {
          newCurrentPage = pageCountBridge.value
        }
        if (isAbsent(props.currentPage)) {
          innerCurrentPage.value = newCurrentPage
        }
        if (hasCurrentPageListener) {
          emit('update:current-page', newCurrentPage)
          emit('on-change', newCurrentPage, pageSizeBridge.value)
        }
      },
    })

    watch(pageCountBridge, (val) => {
      if (currentPageBridge.value > val) currentPageBridge.value = val
    })

    function handleCurrentChange(val: number) {
      currentPageBridge.value = val
    }

    function handleSizeChange(val: number) {
      pageSizeBridge.value = val
      const newPageCount = pageCountBridge.value
      if (currentPageBridge.value > newPageCount) {
        currentPageBridge.value = newPageCount
      }
    }

    function prev() {
      if (props.disabled) return
      currentPageBridge.value -= 1
    }

    function next() {
      if (props.disabled) return
      currentPageBridge.value += 1
    }

    function changePage(page) {
      if (props.disabled) return
      currentPageBridge.value = page
    }

    function handelPageClick(val: number) {
      handleCurrentChange(val)
    }

    function addClass(element: any, cls: string) {
      if (element) {
        if (!element.props) {
          element.props = {}
        }
        element.props.class = [element.props.class, cls].join(' ')
      }
    }

    provide(PageContextKey, {
      pageCount: pageCountBridge,
      disabled: computed(() => props.disabled),
      currentPage: currentPageBridge,
      changeEvent: handleCurrentChange,
      handleSizeChange,
    })

    return () => {
      if (!assertValidUsage.value) {
        debugWarn(componentName, t('sg.page.deprecationWarning'))
        return null
      }
      if (!props.layout) return null
      if (props.hideOnSinglePage && pageCountBridge.value <= 1) return null
      const rootChildren: Array<VNode | VNode[] | null> = []

      const TEMPLATE_MAP: Record<
        Exclude<LayoutKey, '->'>,
        VNode | VNode[] | null
      > = {
        prev: h(Prev, {
          disabled: props.disabled,
          currentPage: currentPageBridge.value,
          prevText: props.prevText,
          onClick: prev,
          onChangePage: changePage,
          showFirstAndLast: props.showFirstAndLast,
        }),
        jumper: h(Jumper, {
          showJumper: props.showJumper,
        }),
        pager: h(Pager, {
          currentPage: currentPageBridge.value,
          pageCount: pageCountBridge.value,
          pagerCount: props.pagerCount,
          onChange: handelPageClick,
          disabled: props.disabled,
          activeColor: props.activeColor,
          activeBackground : props.activeBackground,
          type: props.type,
          isLessPages: props.isLessPages,
        }),
        next: h(Next, {
          disabled: props.disabled,
          currentPage: currentPageBridge.value,
          pageCount: pageCountBridge.value,
          nextText: props.nextText,
          onClick: next,
          onChangePage: changePage,
          showFirstAndLast: props.showFirstAndLast,
        }),
        sizes: h(Sizes, {
          pageSize: pageSizeBridge.value,
          pageSizes: props.pageSizes,
          popperClass: props.popperClass,
          disabled: props.disabled,
          size: props.size,
          placement: props.placement,
          transfer: props.transfer,
          showSizes: props.showSizes,
        }),
        slot: slots?.default?.() ?? null,
        total: h(Total, {
          total: isAbsent(props.total) ? 0 : props.total,
          showTotal: props.showTotal,
        }),
        pages: h(Pages, {
          pages: pageCountBridge.value,
          showPages: props.showPages
        }),
        holder: h(Holder)
      }

      const components = props.layout
        .split(',')
        .map((item: string) => item.trim()) as LayoutKey[]

      components.forEach((c) => {
        rootChildren.push(TEMPLATE_MAP[c])
      })

      addClass(rootChildren[0], 'is-first')
      addClass(rootChildren[rootChildren.length - 1], 'is-last')

      return h(
        'div',
        {
          role: 'page',
          'aria-label': 'page',
          class: [
            'sg-page',
            {
              'sg-page--small': props.size === 'small',
              [`sg-page--${props.type}`]: props.type,
              [`sg-page--${props.justify}`]: true
            }
          ],
          style: props.type && props.activeColor ? {
            '--sg-page-active-color': props.activeColor
          } : {}
        },
        rootChildren
      )
    }
  },
})
